import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { isRunningInNativeApp } from '@muzly/shared';

import { PageHeader } from '../../../components';
import { detectPlatform, storeUrlOf } from '../../link';
import { ResultReport } from '../components/result';
import { useSharedResult } from '../record/useSharedResult';

/**
 * 공유 링크로 열린 결과 — `/share/:id`. 프로필 게이트 **밖**이라 링크를 받은 사람이 설치·가입 없이
 * 본다. 본문은 내 결과 상세와 같은 `ResultReport` 다 — 서버 회차에서 만든 레코드 하나로 그린다.
 *
 * **녹음은 없다** — 재생 카드를 두지 않고 `media$`·`mediaId` 를 읽지도 않는다(서버도 비소유자
 * 응답에서 뺀다). 삭제·공유 버튼도 없다: 남의 회차다. 이력 대비(직전 회차)와 설문 안내는
 * 받는 사람에게 없는 값이라 비어 있다. `TabLayout` 밖이라 `PolicyPage` standalone 처럼 `main` 랜드마크와
 * 바닥 여백을 직접 준다(위 안전 영역은 `PageHeader` 가 든다).
 *
 * "나도 진단하기" 는 모바일 브라우저에서 스토어로 간다(`storeUrlOf` — 운영 빌드만). 앱 안·데스크톱·
 * 개발 빌드에서는 `/` 로 간다 — 프로필이 없으면 게이트가 초기 설정으로 보낸다.
 * 헤더의 `<` 는 온 곳으로 돌아간다 — 앱 안에서 딥링크로 열렸으면 보던 화면, 주소로 바로 열렸으면
 * 돌아갈 항목이 없어 `/` 다(`useGoBack`).
 * 주소의 id 는 서버 조회에만 쓰고 `navigate` 에 넣지 않는다.
 */
export const SharedResultPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id = '' } = useParams<{ id: string }>();
  const shared = useSharedResult(id);

  // 앱 안의 UA 도 모바일이다 — 이미 앱이 있는 사람을 스토어로 보내지 않는다.
  const storeUrl = isRunningInNativeApp() ? null : storeUrlOf(detectPlatform());
  const ctaClass =
    'mt-8 flex h-[54px] w-full items-center justify-center rounded-full bg-black text-base font-bold tracking-[-0.08px] text-white';
  const cta = storeUrl ? (
    <a href={storeUrl} className={ctaClass}>
      {t('web.share.cta')}
    </a>
  ) : (
    <button type="button" onClick={() => navigate('/')} className={ctaClass}>
      {t('web.share.cta')}
    </button>
  );

  return (
    <main className="flex min-h-dvh w-full flex-col bg-background pb-[calc(var(--safe-area-bottom,0px)+2rem)]">
      <PageHeader title={t('web.share.title')} />
      <div className="px-[18px] pt-6">
        {shared.status === 'loading' && (
          <p role="status" className="text-sm text-muted-foreground">
            {t('web.share.loading')}
          </p>
        )}
        {shared.status === 'unavailable' && (
          <>
            <h2 className="text-lg font-semibold">
              {t('web.share.unavailableTitle')}
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {t('web.share.unavailableBody')}
            </p>
            {cta}
          </>
        )}
        {shared.status === 'ready' && (
          <>
            <ResultReport record={shared.record} />
            {cta}
          </>
        )}
      </div>
    </main>
  );
};
